import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import TeamSelector from "@/components/TeamSelector";
import { toast } from "sonner";
import { User, Users, Shield, Search, ChevronRight } from "lucide-react";

const POSITIONS = ["RHP", "LHP", "C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "OF", "UTIL", "DH"];
const PROVINCES = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];
const RECRUITER_TYPES = [
  "NCAA Division I", "NCAA Division II", "NCAA Division III", "NAIA", "JUCO", "U SPORTS", "CCAA",
  "Professional Scout", "MLB Organization", "Provincial Program", "National Team Program", "Academy",
  "Club / Travel Organization", "Independent Scout", "Other",
];

const ROLES = [
  { key: "player", label: "I'm registering myself", sub: "Player", Icon: User },
  { key: "parent", label: "I'm registering my son/daughter", sub: "Parent", Icon: Users },
  { key: "coach", label: "I'm a Coach / Organization", sub: "Team & org tools", Icon: Shield },
  { key: "recruiter", label: "I'm a Recruiter", sub: "Scouting access", Icon: Search },
];

const EMPTY_PLAYER = {
  first_name: "", last_name: "", graduation_year: "", primary_position: "", province: "",
  date_of_birth: "", bats: "", throws: "", secondary_position: "", city: "", school: "",
  current_team_id: "", show_age_publicly: false,
};

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.redirectTo || "/dashboard";

  const [role, setRole] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [player, setPlayer] = useState(EMPTY_PLAYER);
  const [recruiterInfo, setRecruiterInfo] = useState({ organization: "", recruiter_type: "NCAA Division I", title: "", phone: "", message: "" });
  const [campTitle, setCampTitle] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Show camp context if the user arrived from a camp "Register" button.
  useEffect(() => {
    const m = /\/camps\/([^/]+)\/register/.exec(redirectTo || "");
    if (m) api.get(`/camps/${m[1]}`).then(({ data }) => setCampTitle(data.title)).catch(() => {});
  }, [redirectTo]);

  const needsPlayer = role === "player" || role === "parent";
  const up = (k, v) => setPlayer((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (needsPlayer) {
      const req = ["first_name", "last_name", "graduation_year", "primary_position", "province"];
      if (req.some((f) => !String(player[f]).trim())) {
        setError("Please complete the required player fields: First name, Last name, Graduation year, Primary position, Province.");
        return;
      }
    }
    setBusy(true);
    const result = await register(email.trim(), password, name.trim());
    if (!result.ok) { setBusy(false); setError(result.error || "Sign up failed"); return; }

    if (needsPlayer) {
      try {
        const raw = { ...player, graduation_year: Number(player.graduation_year) };
        const payload = Object.fromEntries(Object.entries(raw).filter(([, v]) => v !== "" && v != null));
        await api.post("/players", payload);
      } catch (err) {
        setBusy(false);
        setError(formatApiError(err?.response?.data?.detail) || "Account created, but we couldn't save the player profile. You can add it from your dashboard.");
        return;
      }
    }
    setBusy(false);

    if (role === "coach") { toast.success("Account created — let's claim your organization or team."); navigate("/live/claim"); return; }
    if (role === "recruiter") {
      try {
        await api.post("/recruiter-requests", { ...recruiterInfo });
      } catch (err) {
        setBusy(false);
        setError(formatApiError(err?.response?.data?.detail) || "Account created, but we couldn't submit your recruiter request. Please try again from your dashboard.");
        return;
      }
      setBusy(false);
      toast.success("Account created. Your recruiter access request has been submitted for CBR review.");
      navigate("/dashboard");
      return;
    }
    setBusy(false);
    navigate(redirectTo);
  };

  return (
    <PublicLayout>
      <div className="min-h-[calc(100vh-200px)] bg-[#F8F9FA] flex items-center justify-center py-16 px-6">
        <div className="w-full max-w-xl">
          <div className="bg-white border border-slate-200 p-8 sm:p-10 rounded-sm">
            {campTitle && (
              <div data-testid="signup-camp-context" className="mb-6 bg-[#0A192F] text-white px-4 py-3 rounded-sm">
                <div className="label-overline text-white/50">You're registering for</div>
                <div className="font-display text-xl uppercase tracking-tighter">{campTitle}</div>
              </div>
            )}

            <div className="label-overline text-[#E63946]">Create Account</div>
            <h1 className="font-display text-4xl uppercase tracking-tighter mt-2">Who are you registering?</h1>

            {/* Role selection */}
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {ROLES.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  data-testid={`signup-role-${r.key}`}
                  onClick={() => { setRole(r.key); setError(""); }}
                  className={`flex items-center gap-3 p-4 border rounded-sm text-left transition-colors ${role === r.key ? "border-[#E63946] bg-red-50" : "border-slate-200 hover:border-slate-400"}`}
                >
                  <r.Icon className={`w-5 h-5 shrink-0 ${role === r.key ? "text-[#E63946]" : "text-slate-400"}`} />
                  <div>
                    <div className="text-sm font-semibold text-[#0A192F]">{r.label}</div>
                    <div className="text-xs text-slate-500">{r.sub}</div>
                  </div>
                </button>
              ))}
            </div>

            {error && (
              <div data-testid="signup-error" className="mt-6 border-l-4 border-[#E63946] bg-red-50 p-3 text-sm text-[#E63946]">{error}</div>
            )}

            {role && (
              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="label-overline">Your Full Name</label>
                    <input data-testid="signup-name-input" required value={name} onChange={(e) => setName(e.target.value)} className="mt-2 w-full px-4 py-3 border border-slate-300 rounded-sm focus:border-[#0A192F]" />
                  </div>
                  <div>
                    <label className="label-overline">Email</label>
                    <input data-testid="signup-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 w-full px-4 py-3 border border-slate-300 rounded-sm focus:border-[#0A192F]" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label-overline">Password</label>
                    <input data-testid="signup-password-input" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 w-full px-4 py-3 border border-slate-300 rounded-sm focus:border-[#0A192F]" />
                    <p className="text-xs text-slate-500 mt-1">Minimum 8 characters.</p>
                  </div>
                </div>

                {needsPlayer && (
                  <div className="pt-4 border-t border-slate-200">
                    <h2 className="font-display text-xl uppercase tracking-tight">{role === "player" ? "Your Player Profile" : "Your Player's Profile"}</h2>
                    <p className="text-xs text-slate-500 mt-1">This creates the profile in the CBR player database. Required fields marked *.</p>
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                      <input data-testid="signup-player-first" placeholder="First name *" value={player.first_name} onChange={(e) => up("first_name", e.target.value)} className="px-4 py-3 border border-slate-300 rounded-sm" />
                      <input data-testid="signup-player-last" placeholder="Last name *" value={player.last_name} onChange={(e) => up("last_name", e.target.value)} className="px-4 py-3 border border-slate-300 rounded-sm" />
                      <input data-testid="signup-player-grad" type="number" placeholder="Graduation year * (e.g. 2028)" value={player.graduation_year} onChange={(e) => up("graduation_year", e.target.value)} className="px-4 py-3 border border-slate-300 rounded-sm" />
                      <select data-testid="signup-player-position" value={player.primary_position} onChange={(e) => up("primary_position", e.target.value)} className="px-4 py-3 border border-slate-300 rounded-sm bg-white">
                        <option value="">Primary position *</option>
                        {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                      <select data-testid="signup-player-province" value={player.province} onChange={(e) => up("province", e.target.value)} className="px-4 py-3 border border-slate-300 rounded-sm bg-white">
                        <option value="">Province *</option>
                        {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                      <select value={player.secondary_position} onChange={(e) => up("secondary_position", e.target.value)} className="px-4 py-3 border border-slate-300 rounded-sm bg-white">
                        <option value="">Secondary position (optional)</option>
                        {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                      <input type="date" title="Date of birth" value={player.date_of_birth} onChange={(e) => up("date_of_birth", e.target.value)} className="px-4 py-3 border border-slate-300 rounded-sm" />
                      <select value={player.bats} onChange={(e) => up("bats", e.target.value)} className="px-4 py-3 border border-slate-300 rounded-sm bg-white">
                        <option value="">Bats (optional)</option><option value="R">Right</option><option value="L">Left</option><option value="S">Switch</option>
                      </select>
                      <select value={player.throws} onChange={(e) => up("throws", e.target.value)} className="px-4 py-3 border border-slate-300 rounded-sm bg-white">
                        <option value="">Throws (optional)</option><option value="R">Right</option><option value="L">Left</option>
                      </select>
                      <input placeholder="City (optional)" value={player.city} onChange={(e) => up("city", e.target.value)} className="px-4 py-3 border border-slate-300 rounded-sm" />
                      <input placeholder="School (optional)" value={player.school} onChange={(e) => up("school", e.target.value)} className="px-4 py-3 border border-slate-300 rounded-sm sm:col-span-2" />
                    </div>
                    <div className="mt-4">
                      <div className="label-overline text-slate-500 mb-1">Current Team (optional)</div>
                      <TeamSelector value={player.current_team_id} onChange={(t) => up("current_team_id", t?.id || "")} testidPrefix="signup-team" />
                    </div>
                    <label className="mt-3 flex items-center gap-2 text-xs text-slate-600">
                      <input data-testid="signup-show-age" type="checkbox" checked={player.show_age_publicly} onChange={(e) => up("show_age_publicly", e.target.checked)} />
                      Show age (not birthdate) on public profile. Off by default; DOB is always private.
                    </label>
                  </div>
                )}

                {role === "recruiter" && (
                  <div className="pt-4 border-t border-slate-200">
                    <h2 className="font-display text-xl uppercase tracking-tight">Recruiter Details</h2>
                    <p className="text-xs text-slate-500 mt-1">Recruiter access is reviewed and approved by CBR staff to protect player data.</p>
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                      <input data-testid="signup-recruiter-org" placeholder="Organization / School" value={recruiterInfo.organization} onChange={(e) => setRecruiterInfo({ ...recruiterInfo, organization: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-sm" />
                      <select data-testid="signup-recruiter-type" value={recruiterInfo.recruiter_type} onChange={(e) => setRecruiterInfo({ ...recruiterInfo, recruiter_type: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-sm bg-white">
                        {RECRUITER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <input data-testid="signup-recruiter-title" placeholder="Title (e.g. Head Coach)" value={recruiterInfo.title} onChange={(e) => setRecruiterInfo({ ...recruiterInfo, title: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-sm" />
                      <input data-testid="signup-recruiter-phone" placeholder="Phone (optional)" value={recruiterInfo.phone} onChange={(e) => setRecruiterInfo({ ...recruiterInfo, phone: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-sm" />
                      <textarea data-testid="signup-recruiter-message" placeholder="Anything to help us verify you? (optional)" value={recruiterInfo.message} onChange={(e) => setRecruiterInfo({ ...recruiterInfo, message: e.target.value })} rows={2} className="px-4 py-3 border border-slate-300 rounded-sm sm:col-span-2" />
                    </div>
                  </div>
                )}

                {(role === "coach" || role === "recruiter") && (
                  <p className="text-xs text-slate-500">
                    {role === "coach"
                      ? "After creating your account, you'll be taken to claim your organization or team."
                      : "Your account will be created immediately, and your recruiter request will be sent to CBR for approval. You'll get full portal access once approved."}
                  </p>
                )}

                <button data-testid="signup-submit-button" type="submit" disabled={busy} className="w-full bg-[#E63946] hover:bg-[#D90429] text-white py-3 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60 inline-flex items-center justify-center gap-2">
                  {busy ? "Creating account…" : (needsPlayer ? "Create Account & Continue" : "Create Account")}
                  {!busy && <ChevronRight className="w-4 h-4" />}
                </button>
              </form>
            )}

            <div className="mt-6 text-center text-sm text-slate-600">
              Already have an account?{" "}
              <Link to="/login" state={{ redirectTo }} className="text-[#E63946] font-semibold uppercase tracking-wider text-xs">Sign in</Link>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
